var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Generic record form component.
 * Renders tabbed MARC form (tab bar, DynamicTab, MarcPreviewPanel,
 * validation display, dirty tracking, cancel confirmation, error banner).
 *
 * Decoupled from proposal-specific types — caller provides save/cancel callbacks.
 * Used by ProposalForm (proposals) and future RecordEdit (direct record editing).
 *
 * Related UCs: AR-01, AR-02, AR-10
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useForm, useFormState } from 'react-hook-form';
import { Pane, Paneset, Button, ButtonGroup, PaneFooter, ConfirmationModal, Modal, Callout, PaneMenu, } from '@folio/stripes/components';
import { useOkapiKy } from '@folio/stripes/core';
import { stateKey } from './config/fieldDefinitionTypes';
import { ProposeAuthorityProvider } from './ProposeAuthorityContext';
import { saveSnapshot, loadSnapshot } from './proposeAuthorityFlow';
import { countIssuesOnTab, locateField, shownTabs } from './utils/locateField';
import { headingNameQuery } from './utils/headingNameQuery';
import { applyLinkedHeadingCopy, copiesLinkedHeading, linkedHeadingCopy } from './utils/linkedHeadingCopy';
import { fetchRecord } from '../../api/recordService';
import SimilarHeadingsHint from './fields/SimilarHeadingsHint';
import HeadingRequiredHint from './fields/HeadingRequiredHint';
import { tabBarStyle } from './stickyLayout';
import { useSimilarHeadings } from '../../hooks/useSimilarHeadings';
import { useHeadingPreview } from '../../hooks/useHeadingPreview';
import DynamicTab from './renderer/DynamicTab';
import { SubfieldCatalogProvider } from './renderer/SubfieldCatalogContext';
import { resolveLabel } from './renderer/labelResolver';
import ProcessorInfo from './fields/ProcessorInfo';
import MarcPreviewPanel from './MarcPreviewPanel';
import DuplicateCompareModal from './fields/DuplicateCompareModal';
import { genericFormToProposalData } from './utils/genericFormToProposalData';
import { proposalDataToRecordDataFields } from './utils/proposalDataToRecordDataFields';
import { filterEmptyPreviewFields } from './utils/filterEmptyPreviewFields';
import { isControlFieldTag } from '../../utils/marcControlFields';
import ActionHelp from '../ActionHelp';
import { SaveButtonLabel, SaveInFlightAnnouncement } from '../SaveInFlight/SaveInFlight';
import SaveOutcomeUnknownNotice from './SaveOutcomeUnknownNotice';
import { isSaveOutcomeUnknownError, toSaveFailure, } from '../../utils/saveOutcomeUnknown';
// Side-effect import: registers built-in action handlers (TASK-044
// fetchUrlMetadata) on module load so DynamicFieldPanel can find them by name.
import './actions/actionHandlers';
/** Breakpoint below which MARC preview panel is hidden (single-column fallback). */
var SPLIT_LAYOUT_BREAKPOINT = 1200;
var tabIssueBadgeStyle = {
    marginLeft: '6px',
    padding: '0 6px',
    borderRadius: '8px',
    background: '#c0392b',
    color: '#fff',
    fontSize: '0.8em',
    fontWeight: 700,
};
var tabWarningBadgeStyle = __assign(__assign({}, tabIssueBadgeStyle), { background: '#fbf3e2', color: '#8a5d00', border: '1px solid #8a5d00' });
var tabStaleBadgeStyle = __assign(__assign({}, tabIssueBadgeStyle), { background: '#eef0f3', color: '#5d6570', border: '1px solid #d9dde3' });
/** Read by a screen reader (and by ui-probe's accessibility tree), not shown. */
var visuallyHiddenStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
};
var isPlainObject = function (value) { return (!!value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype); };
/** Plain-data deep copy of form state (objects and arrays; everything else is a leaf). */
var deepCopy = function (value) {
    if (Array.isArray(value))
        return value.map(deepCopy);
    if (isPlainObject(value)) {
        return Object.fromEntries(Object.entries(value).map(function (_a) {
            var k = _a[0], v = _a[1];
            return [k, deepCopy(v)];
        }));
    }
    return value;
};
/**
 * The deepest paths at which `current` differs from `saved`, each with its `current` value.
 * Objects are walked key by key and arrays index by index while both sides have the same
 * shape; where the shape differs (a repeat added, a value turned into an object) the path
 * itself is the leaf.
 */
var changedLeaves = function (current, saved, path) {
    if (path === void 0) { path = ''; }
    var at = function (key) { return (path ? "".concat(path, ".").concat(key) : String(key)); };
    if (isPlainObject(current) && isPlainObject(saved)) {
        var keys = Array.from(new Set(__spreadArray(__spreadArray([], Object.keys(current), true), Object.keys(saved), true)));
        return keys.flatMap(function (key) { return changedLeaves(current[key], saved[key], at(key)); });
    }
    if (Array.isArray(current) && Array.isArray(saved) && current.length === saved.length) {
        return current.flatMap(function (item, index) { return changedLeaves(item, saved[index], at(index)); });
    }
    return JSON.stringify(current) === JSON.stringify(saved) ? [] : [{ path: path, value: current }];
};
/**
 * The nearest ancestor that scrolls vertically — in Stripes the pane's content, not the
 * window — or null when only the document does.
 */
var scrollParentOf = function (element) {
    for (var node = element.parentElement; node; node = node.parentElement) {
        var overflowY = window.getComputedStyle(node).overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
            return node;
        }
    }
    return null;
};
/**
 * Generic config-driven record form.
 * Handles tab navigation, DynamicTab rendering, MARC preview,
 * cancel confirmation, error banner, and save loading state.
 */
var RecordForm = function (_a) {
    var _b;
    var config = _a.config, recordType = _a.recordType, initialData = _a.initialData, excludeAuthorityId = _a.excludeAuthorityId, excludeProposalId = _a.excludeProposalId, _c = _a.isEdit, isEdit = _c === void 0 ? false : _c, _d = _a.mode, mode = _d === void 0 ? 'create' : _d, _e = _a.targetRecordAuthorityIdDisplay, targetRecordAuthorityIdDisplay = _e === void 0 ? null : _e, previewControlFields = _a.previewControlFields, previewRecordType = _a.previewRecordType, metadata = _a.metadata, onSave = _a.onSave, onSaveAndApprove = _a.onSaveAndApprove, reviewHelpValues = _a.reviewHelpValues, onPublish = _a.onPublish, onSubmitForReview = _a.onSubmitForReview, onCheck = _a.onCheck, confirmNotice = _a.confirmNotice, onCheckStale = _a.onCheckStale, runCheckRef = _a.runCheckRef, headerActions = _a.headerActions, checkPanel = _a.checkPanel, onSaveAndReject = _a.onSaveAndReject, _f = _a.saveError, saveError = _f === void 0 ? null : _f, onCancel = _a.onCancel, _g = _a.hideSaveButton, hideSaveButton = _g === void 0 ? false : _g, extraFooterActions = _a.extraFooterActions, paneTitleOverride = _a.paneTitle, paneSubOverride = _a.paneSub, saveButtonLabelKey = _a.saveButtonLabelKey, _h = _a.leaveModalHeadingKey, leaveModalHeadingKey = _h === void 0 ? 'ui-czna-authorities.proposalForm.leaveModal.heading' : _h, _j = _a.leaveModalMessageKey, leaveModalMessageKey = _j === void 0 ? 'ui-czna-authorities.proposalForm.leaveModal.message' : _j, _k = _a.paneId, paneId = _k === void 0 ? 'recordFormPane' : _k, _l = _a.embedded, embedded = _l === void 0 ? false : _l, requestCloseRef = _a.requestCloseRef, leaveWithoutAskingRef = _a.leaveWithoutAskingRef, markSavedRef = _a.markSavedRef, returnTo = _a.returnTo, restoreKey = _a.restoreKey, visibleTabIds = _a.visibleTabIds, validationIssues = _a.validationIssues, validationIssuesStale = _a.validationIssuesStale, jumpToFieldRef = _a.jumpToFieldRef, banners = _a.banners;
    var intl = useIntl();
    var history = useHistory();
    var _m = useState(0), activeTabIndex = _m[0], setActiveTabIndex = _m[1];
    var _o = useState(false), showCancelModal = _o[0], setShowCancelModal = _o[1];
    /*
     * CZNA-1466 — the namesake being compared, with the record being written frozen
     * as MARC at the moment the window opened. The window is modal, so the form
     * cannot change underneath it, and a snapshot keeps it from recomputing on
     * every render of the form.
     */
    var _p = useState(null), comparing = _p[0], setComparing = _p[1];
    var _q = useState(false), showSaveAndApproveModal = _q[0], setShowSaveAndApproveModal = _q[1];
    var _r = useState(false), showPublishModal = _r[0], setShowPublishModal = _r[1];
    var _s = useState(false), showSubmitForReviewModal = _s[0], setShowSubmitForReviewModal = _s[1];
    var _t = useState({}), validationErrors = _t[0], setValidationErrors = _t[1];
    /**
     * The failure of the save this component ran. CZNA-1625: a plain sentence, or — when the
     * server did not answer in time — the structured error whose banner offers a way to find out
     * whether the save happened (`toSaveFailure` keeps it whole).
     */
    var _u = useState(null), saveErrorMessage = _u[0], setSaveErrorMessage = _u[1];
    /** CZNA-1625 — tells a new failure's banner from the previous one's (its verify state). */
    var failureSeqRef = useRef(0);
    var errorBannerRef = useRef(null);
    /*
     * Set for exactly one transition, by the confirm handler, and cleared by the
     * block itself. The alternative — calling `unblock()` on confirm — leaves the
     * form UNGUARDED whenever the navigation does not actually unmount it, and
     * the leave-guard effect further down would not re-register because `isDirty`
     * never changed.
     */
    var allowLeaveRef = useRef(false);
    var _v = useState(false), isSaving = _v[0], setIsSaving = _v[1];
    /**
     * CZNA-1508 — which button's write is in flight, for its "Ukládá se…". Separate from
     * `isSaving`, which also covers a check (CZNA-1518, nothing written) and outlives the write
     * itself through whatever the host does after it; this is only the write. A grey button
     * alone read as a frozen form for as long as a slow PUT took.
     */
    var _w = useState(null), writingAction = _w[0], setWritingAction = _w[1];
    /** CZNA-1508 — a late `written` from an earlier save must not end a later one's claim. */
    var writeAttemptRef = useRef(0);
    var startWriting = function (action) {
        writeAttemptRef.current += 1;
        setWritingAction(action);
    };
    var stopWriting = function () { return setWritingAction(null); };
    // Track viewport width for responsive layout
    var _x = useState(typeof window !== 'undefined' ? window.innerWidth >= SPLIT_LAYOUT_BREAKPOINT : true), isWideViewport = _x[0], setIsWideViewport = _x[1];
    useEffect(function () {
        var handleResize = function () {
            setIsWideViewport(window.innerWidth >= SPLIT_LAYOUT_BREAKPOINT);
        };
        window.addEventListener('resize', handleResize);
        return function () { return window.removeEventListener('resize', handleResize); };
    }, []);
    var ky = useOkapiKy();
    var _y = useForm({
        defaultValues: initialData,
    }), watch = _y.watch, control = _y.control, getValues = _y.getValues, setValue = _y.setValue, reset = _y.reset;
    // Callout for action-button feedback (TASK-044). Subfield action handlers
    // surface success/warning/error toasts here rather than blocking the form
    // with banners.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var calloutRef = React.useRef(null);
    var showToast = React.useCallback(function (type, messageId) {
        var _a;
        (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
            type: type,
            message: intl.formatMessage({ id: messageId }),
        });
    }, [intl]);
    // CZNA-241: propose-GA-from-picker. The picker (deep in the tree) calls this
    // via ProposeAuthorityContext with its own subfield path + the typed seed. We
    // snapshot the parent form's full state to sessionStorage and navigate to the
    // separate GA create route (its own root Paneset — no overlay over this
    // editor). On return, the restore effect below rehydrates + writes the result
    // back into `target`.
    var proposeAuthority = React.useCallback(function (target, seedText, targetRecordType) {
        if (!returnTo)
            return;
        // CZNA-319: the target authority type comes from the picker subfield
        // (`subfieldDef.recordType`), NOT a hardcoded RecordType.GEOGRAPHIC. The
        // child create route is opened with `?type=<targetRecordType>`, so the same
        // mechanic serves any allow-listed picker target.
        var token = saveSnapshot({
            returnTo: returnTo,
            parentRecordType: recordType,
            targetRecordType: targetRecordType,
            formValues: getValues(),
            activeTabIndex: activeTabIndex,
            target: target,
        });
        var params = new URLSearchParams({
            type: targetRecordType,
            seed: seedText,
            flow: token,
        });
        // CZNA-321: the one exit that loses nothing — the values ride in the snapshot
        // and come back on return — so it must not trip the unsaved-changes guard
        // below, which otherwise stops every parent that has been typed into. The
        // block runs synchronously inside `push`, so the flag is lowered right after
        // for the case where no block was registered (a clean form) to consume it.
        allowLeaveRef.current = true;
        history.push("/czna-authorities/proposals/create?".concat(params.toString()));
        allowLeaveRef.current = false;
    }, [returnTo, recordType, getValues, activeTabIndex, history]);
    // CZNA-241: one-shot rehydration on return from the GA create route. Guarded by
    // a ref so a double-effect (React 18 StrictMode) cannot consume the snapshot
    // twice; loadSnapshot itself also deletes the key as a second line of defence.
    var restoredKeyRef = useRef(null);
    useEffect(function () {
        if (!restoreKey || restoredKeyRef.current === restoreKey)
            return;
        restoredKeyRef.current = restoreKey;
        var snapshot = loadSnapshot(restoreKey);
        if (!snapshot)
            return;
        // Restore the in-progress parent state.
        reset(snapshot.formValues);
        setActiveTabIndex(snapshot.activeTabIndex);
        if (snapshot.result) {
            // Write the new GA's FK + heading into the exact 370 subfield the propose
            // was triggered from. shouldDirty so the user is prompted to save.
            setValue(snapshot.target, { value: snapshot.result.heading, sourceAuthorityId: snapshot.result.id }, { shouldDirty: true });
            // CZNA-1430: a name field then takes the new authority's heading part by part, as a
            // pick from the register does. Written whole first, so a failed read leaves the
            // value this path always wrote.
            var _a = snapshot.target.split('.'), key_1 = _a[1], index = _a[2], code_1 = _a[3];
            var fieldDef_1 = config.tabs.flatMap(function (tab) { return tab.fields; }).find(function (fd) { return stateKey(fd) === key_1; });
            if (fieldDef_1 && copiesLinkedHeading(fieldDef_1.tag, code_1, fieldDef_1.subfields)) {
                var id_1 = snapshot.result.id;
                var instancePath_1 = "fields.".concat(key_1, ".").concat(index);
                var written_1 = JSON.stringify(getValues(instancePath_1));
                fetchRecord(ky, id_1)
                    .then(function (detail) {
                    // Any part of the field changed while the read was out: what the user did wins.
                    if (JSON.stringify(getValues(instancePath_1)) !== written_1)
                        return;
                    var copy = linkedHeadingCopy(detail, fieldDef_1);
                    if (!copy)
                        return;
                    var value = applyLinkedHeadingCopy(copy, fieldDef_1.tag, instancePath_1, code_1, getValues, setValue);
                    setValue(snapshot.target, { value: value, sourceAuthorityId: id_1 }, { shouldDirty: true });
                })
                    .catch(function () { });
            }
            // Default return UX: scroll to the filled field + success toast. The
            // subfield is locatable only by its `data-field-path` attribute (no element
            // carries an `id` for it), so query by that path directly.
            // Defer so the DOM has the restored field mounted before we scroll/focus.
            setTimeout(function () {
                var el = document.querySelector("[data-field-path=\"".concat(snapshot.target, "\"]"));
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 0);
            showToast('success', 'ui-czna-authorities.recordPicker.proposeAuthority.returnSuccess');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restoreKey]);
    // Read isDirty via useFormState({ control }) instead of destructuring
    // formState from useForm. The parent-useForm formState Proxy subscription
    // is unreliable for deeply nested paths under our subfield map shape
    // (`fields.{tag}.{idx}.{code}.value` / `.components.{type}`); RHF v7's
    // documented escape hatch is useFormState, which re-establishes its own
    // Proxy subscription on each render of its caller (TASK-118).
    var isDirty = useFormState({ control: control }).isDirty;
    /*
     * CZNA-1032 — leaving a dirty form.
     *
     * `handleCancel` guards the footer "Zrušit" and the embedded X
     * (requestCloseRef). Neither is on the path NK took: a click in the menu is a
     * plain router transition, so the form unmounted and the unsaved work went
     * with it (P. Šťastná, 7. 9. 2026 — a detour to Rejstříky mid-proposal).
     *
     * Why not react-router's `<Prompt>`: it delegates to the router's
     * `getUserConfirmation`, which is `window.confirm` — a raw browser alert that
     * looks nothing like the rest of the application, cannot say more than one
     * line and cannot label its own buttons. Stripes owns the Router, so we
     * cannot swap that hook out.
     *
     * So the block cancels the transition SILENTLY and we ask ourselves.
     * `confirmTransitionTo` (history 4.10) treats a prompt returning `false` as a
     * cancel and never consults `getUserConfirmation`; returning `undefined` lets
     * the transition through. That gives two states and one flag between them.
     */
    var _z = useState(null), pendingLeave = _z[0], setPendingLeave = _z[1];
    /*
     * CZNA-1044 — the exits the cataloguer has ALREADY confirmed.
     *
     * `onCancel` is a router transition in every routed host (`VocabularyEdit`
     * and `ProposalEdit` push back to the record's detail page), so the block
     * above cannot tell it apart from the menu click it exists to catch. The
     * result was two dialogs for one decision: the pane's own "Zahodit"
     * question, then the guard's "Opustit rozpracovaný návrh?" — and the
     * navigation cancelled underneath both (measured on the vocabulary editor's
     * L1 spec, GitLab job 134957). The comment above says `handleCancel` guards
     * the footer "Zrušit" and the embedded X; that is exactly why the guard must
     * stand aside here — the question has been asked one dialog earlier.
     *
     * Why the permission is LOWERED again rather than left for the next
     * transition to consume: `onCancel` need not navigate at all (a host that
     * only closes a surrounding Modal — the `embedded` shape, which today has no
     * non-test caller in this module), and a permission left standing would then
     * be spent on whatever navigation came next, leaving one real exit from a
     * dirty form unguarded.
     *
     * WHEN it is lowered is the part that has to survive hosts nobody has written
     * yet. `onCancel` is typed `() => void`, which accepts an `async` function
     * without complaint, so a future host may well reach `history` only after a
     * couple of awaits (`await invalidateQueries(...)`, then push). A fixed
     * microtask would have lowered the flag before that push and handed
     * CZNA-1044 straight back, silently. So the lifetime is tied to the
     * OPERATION: a thenable return lowers on settle, anything else lowers on the
     * next microtask, which is the whole window a synchronous push needs —
     * `history.block`'s prompt runs synchronously inside `history.push`, so a
     * transition that was going to happen has already consumed the permission by
     * then. The accepted cost of the thenable branch is that a promise which
     * never settles keeps the permission raised; that beats the alternative of
     * re-asking on a discard the cataloguer already confirmed.
     */
    var leaveWithoutAsking = function (navigate) {
        allowLeaveRef.current = true;
        var lower = function () { allowLeaveRef.current = false; };
        var result;
        try {
            result = navigate();
        }
        catch (err) {
            // A host that threw never navigated, so the permission has no transition
            // left to belong to.
            lower();
            throw err;
        }
        var thenable = result;
        if (thenable && typeof thenable.then === 'function') {
            thenable.then(lower, lower);
        }
        else {
            Promise.resolve().then(lower);
        }
        // CZNA-1043 — returned so a caller resuming its OWN deferred save (the
        // WARNING-modal confirm, which never goes through `handleSave` below) can
        // `await` the same call this wraps instead of racing a second one. The four
        // in-file callers below discard it, exactly as before.
        return result;
    };
    useEffect(function () {
        if (!isDirty || !history)
            return undefined;
        var unblock = history.block(function (location, action) {
            if (allowLeaveRef.current) {
                allowLeaveRef.current = false;
                return undefined;
            }
            setPendingLeave({
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                action: action,
            });
            return false;
        });
        return unblock;
    }, [history, isDirty]);
    var replayLeave = function (target) {
        // A blocked POP has already been reverted by history, so replaying it means
        // going back again — pushing the target would leave a bogus forward entry.
        if (target.action === 'POP') {
            history.go(-1);
        }
        else if (target.action === 'REPLACE') {
            history.replace({ pathname: target.pathname, search: target.search, hash: target.hash });
        }
        else {
            history.push({ pathname: target.pathname, search: target.search, hash: target.hash });
        }
    };
    var handleConfirmLeave = function () {
        var target = pendingLeave;
        setPendingLeave(null);
        if (!target)
            return;
        allowLeaveRef.current = true;
        replayLeave(target);
    };
    /*
     * CZNA-1450 — what the save callbacks receive is a DEEP copy. `getValues()` copies only
     * the top level; everything under `fields` is the form's live object, which RHF mutates
     * in place on the next keystroke. A parent that holds on to it across an `await` — and
     * `markSavedRef` asks it to, to report what was written — would otherwise report the
     * values typed during the save as saved, and the guard would forget them.
     */
    var snapshotValues = function () { return deepCopy(getValues()); };
    /*
     * CZNA-1450 — the values the parent reports as written become the form's new baseline.
     *
     * `keepValues`: the fields keep exactly what is in them — no value is written back, so
     * the caret, an expanded subfield, the active tab and every derived value stay as the
     * cataloguer left them. Only `defaultValues` move, which is what `isDirty` is measured
     * against, so the leave guard and the cancel dialog stand down until the next edit.
     *
     * The save is asynchronous and the fields stay editable while it runs, so what is in
     * the form now may already differ from what was written. Such an edit is a real
     * unsaved change and must stay guarded (CZNA-98/1032): `reset` clears `isDirty`
     * unconditionally, so each value that differs is set again with its own current
     * content — identical, but `setValue` recomputes dirtiness against the new baseline.
     * Only the leaves that differ: setting `fields` whole would push every subfield back
     * through the form, which is the rewrite the baseline move exists to avoid. The
     * comparison is by serialisation; a false "differs" only costs a dialog.
     */
    var savedInPlaceRef = useRef(false);
    var markSaved = function (saved) {
        var current = snapshotValues();
        reset(saved, { keepValues: true });
        var changed = changedLeaves(current, saved);
        changed.forEach(function (_a) {
            var path = _a.path, value = _a.value;
            setValue(path, value, { shouldDirty: true });
        });
        savedInPlaceRef.current = changed.length === 0;
    };
    if (markSavedRef) {
        markSavedRef.current = markSaved;
    }
    /*
     * CZNA-1450 — "Uložit a odejít" after a save that kept the form open. The navigation is
     * replayed from an effect rather than right after the save: by the time it runs, the
     * clean render has already unregistered the `history.block` above, so the transition
     * goes through without borrowing `allowLeaveRef` — a permission raised for a block that
     * no longer exists would be spent on some later, unrelated exit.
     */
    var _0 = useState(null), leaveAfterSave = _0[0], setLeaveAfterSave = _0[1];
    useEffect(function () {
        if (!leaveAfterSave || isDirty)
            return;
        setLeaveAfterSave(null);
        replayLeave(leaveAfterSave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leaveAfterSave, isDirty]);
    /*
     * Closing the tab or reloading happens outside the router, so no window of
     * ours can be shown — this is the one path where the browser's own dialog is
     * the only option. Read through a ref rather than closing over `isDirty`: the
     * listener registers once, and a dependency on `isDirty` would re-register it
     * on every keystroke that flips dirtiness.
     */
    var isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;
    useEffect(function () {
        var handler = function (e) {
            if (isDirtyRef.current) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handler);
        return function () { return window.removeEventListener('beforeunload', handler); };
    }, []);
    /*
     * CZNA-1418 — the heading preview is composed by the backend's `HeadingComposer`
     * from the rows the save sends, so the pane title shows the heading the record will
     * store and nothing else. It used to be composed here, by a TypeScript copy of the
     * composer that dropped a repeated `$p` of a work's title (see `useHeadingPreview`).
     */
    var headingPreview = useHeadingPreview(config, recordType, watch);
    var headingValue = headingPreview.heading;
    /*
     * CZNA-1034 — "is this person already in the base?", asked from the NAME
     * alone rather than from the composed heading above.
     *
     * The two questions are not the same, which is the whole reason this exists:
     * `DUPLICATE_HEADING` (CZNA-221) matches the composed form, so a candidate of
     * `Ondroušek, Antonín,` warns about nothing while two such authorities exist
     * under different dates — and a record whose dates are uncertain, the case NK
     * was testing, can never collide on the composed form at all.
     *
     * Which subfield is "the name" is the config's `headingPreview.similarNameSubfield`;
     * a config without one does not ask (CORPORATE and MEETING — nothing measured
     * asked for them). Its value comes from the same rows the heading was composed
     * from (CZNA-1418), so it is the `$a` the record will hold.
     */
    var headingSubfieldA = headingPreview.nameValue;
    var similarHeadings = useSimilarHeadings(recordType, headingNameQuery(headingSubfieldA), { excludeAuthorityId: excludeAuthorityId, excludeProposalId: excludeProposalId });
    /**
     * CZNA-1518 — the values the host's last verdict judged, serialised; null when no verdict
     * is up. The first change that moves the form off them makes the verdict stale.
     * Compared by value, not by event type: a picked vocabulary term arrives via setValue,
     * and a setValue that writes what is already there changes nothing that was checked.
     */
    var checkedValuesRef = useRef(null);
    var onCheckStaleRef = useRef(onCheckStale);
    onCheckStaleRef.current = onCheckStale;
    useEffect(function () {
        var subscription = watch(function () {
            var _a;
            if (checkedValuesRef.current === null)
                return;
            if (JSON.stringify(getValues()) === checkedValuesRef.current)
                return;
            checkedValuesRef.current = null;
            (_a = onCheckStaleRef.current) === null || _a === void 0 ? void 0 : _a.call(onCheckStaleRef);
        });
        return function () { return subscription.unsubscribe(); };
    }, [watch, getValues]);
    /**
     * CZNA-1518 — the host has just reported on `values`. Typed while it was working out its
     * answer: the verdict is already about other values.
     */
    var verdictJudged = function (values) {
        var _a;
        var judged = JSON.stringify(values);
        if (JSON.stringify(getValues()) === judged) {
            checkedValuesRef.current = judged;
            return;
        }
        checkedValuesRef.current = null;
        (_a = onCheckStaleRef.current) === null || _a === void 0 ? void 0 : _a.call(onCheckStaleRef);
    };
    /*
     * CZNA-581 follow-up — a save that succeeds and navigates away must not trip its
     * own unsaved-changes guard. `onSave` (and its siblings below) is the PARENT's
     * closure: on a clean create/update it calls `history.push` itself, while
     * `isDirty` is still true from the render before the write landed — the
     * `history.block` effect (still registered from that render) intercepted that
     * push and put up "Opustit rozpracovaný návrh?" under a save that had already
     * succeeded and already shown its own success toast (NK, 11. 9. 2026: "hláška,
     * že bylo uloženo" right above a dialog asking whether to discard it).
     *
     * The fix reuses `allowLeaveRef` — the exact mechanism `leaveWithoutAsking`
     * already uses for a confirmed Cancel — around the call: raised before, and
     * lowered again once the call settles, mirroring `leaveWithoutAsking` itself.
     * Lowering unconditionally in `finally` is correct whether or not a navigation
     * actually happened — a save that stays on the form (blocking-errors banner,
     * needsReview) never reaches `history.block` at all, so the ref sits unused.
     */
    var handleSave = function () { return __awaiter(void 0, void 0, void 0, function () {
        var attempt, values, err_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setIsSaving(true);
                    startWriting('save');
                    attempt = writeAttemptRef.current;
                    allowLeaveRef.current = true;
                    values = snapshotValues();
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onSave(values, {
                            written: function () { if (writeAttemptRef.current === attempt)
                                stopWriting(); },
                        })];
                case 2:
                    _a.sent();
                    verdictJudged(values);
                    return [3 /*break*/, 5];
                case 3:
                    err_1 = _a.sent();
                    verdictJudged(values);
                    // Caller throws with a user-facing message (CZNA-1625: or a save of unknown outcome)
                    setSaveErrorMessage(toSaveFailure(err_1, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' })));
                    return [3 /*break*/, 5];
                case 4:
                    setIsSaving(false);
                    stopWriting();
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    /*
     * CZNA-1124 — the leave dialog's third action. What "save" means is entirely
     * the host's (`onSave`'s) decision, same as the plain footer Save button
     * above — this just closes the leave dialog first so a validation failure
     * (handleSave sets `saveErrorMessage` and returns; it never throws past here)
     * leaves the cataloger looking at the form and the error banner, not at a
     * dialog that reappeared on top of them.
     *
     * CZNA-1450 — a save that lands but keeps the form open (blocking errors, needsReview)
     * has no destination of its own, so the leave the dialog was opened for is completed
     * here once the parent has reported the write (`markSavedRef`). A save that navigates
     * goes where it goes; one that failed or was parked behind the WARNING modal never
     * reports, and the cataloguer stays on the form with the banner or the modal.
     */
    var handleSaveAndLeave = function () { return __awaiter(void 0, void 0, void 0, function () {
        var target;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    target = pendingLeave;
                    setPendingLeave(null);
                    savedInPlaceRef.current = false;
                    return [4 /*yield*/, handleSave()];
                case 1:
                    _a.sent();
                    if (target && savedInPlaceRef.current) {
                        setLeaveAfterSave(target);
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var handleSaveAndApprove = function () { return __awaiter(void 0, void 0, void 0, function () {
        var err_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onSaveAndApprove)
                        return [2 /*return*/];
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setShowSaveAndApproveModal(false);
                    setIsSaving(true);
                    startWriting('saveAndApprove');
                    allowLeaveRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onSaveAndApprove(snapshotValues())];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    err_2 = _a.sent();
                    setSaveErrorMessage(toSaveFailure(err_2, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' })));
                    return [3 /*break*/, 5];
                case 4:
                    setIsSaving(false);
                    stopWriting();
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    /**
     * CZNA-1518 — run the host's check with the buttons inert. `true` = the record would
     * pass; `false` = it would not, or the check could not run (the message is up).
     */
    var runCheck = function (intent) { return __awaiter(void 0, void 0, void 0, function () {
        var values, clean, err_3;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onCheck)
                        return [2 /*return*/, true];
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setIsSaving(true);
                    checkedValuesRef.current = null;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    values = snapshotValues();
                    return [4 /*yield*/, onCheck(values, intent)];
                case 2:
                    clean = (_a.sent()) === 'clean';
                    verdictJudged(values);
                    return [2 /*return*/, clean];
                case 3:
                    err_3 = _a.sent();
                    setSaveErrorMessage(toSaveFailure(err_3, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' })));
                    return [2 /*return*/, false];
                case 4:
                    setIsSaving(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var handleCheck = function () { runCheck('check'); };
    if (runCheckRef)
        runCheckRef.current = handleCheck;
    /**
     * CZNA-1518 — a check whose verdict still describes the form. The fields stay editable
     * while the check runs, and a confirm opened on its answer would name warnings about
     * values that are gone; a verdict overtaken by typing is asked for again, once.
     */
    var checkCurrentValues = function (intent) { return __awaiter(void 0, void 0, void 0, function () {
        var attempt;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onCheck)
                        return [2 /*return*/, true];
                    attempt = 0;
                    _a.label = 1;
                case 1:
                    if (!(attempt < 2)) return [3 /*break*/, 4];
                    return [4 /*yield*/, runCheck(intent)];
                case 2:
                    // eslint-disable-next-line no-await-in-loop
                    if (!(_a.sent()))
                        return [2 /*return*/, false];
                    if (checkedValuesRef.current !== null)
                        return [2 /*return*/, true];
                    _a.label = 3;
                case 3:
                    attempt += 1;
                    return [3 /*break*/, 1];
                case 4: return [2 /*return*/, false];
            }
        });
    }); };
    /** CZNA-1518 — the confirm dialog only for a record that can actually be published. */
    var handlePublishClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, checkCurrentValues('publish')];
                case 1:
                    if (_a.sent())
                        setShowPublishModal(true);
                    return [2 /*return*/];
            }
        });
    }); };
    /** @see handlePublishClick */
    var handleSubmitForReviewClick = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, checkCurrentValues('submit')];
                case 1:
                    if (_a.sent())
                        setShowSubmitForReviewModal(true);
                    return [2 /*return*/];
            }
        });
    }); };
    /** @see onPublish */
    var handlePublish = function () { return __awaiter(void 0, void 0, void 0, function () {
        var err_4;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onPublish)
                        return [2 /*return*/];
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setShowPublishModal(false);
                    setIsSaving(true);
                    startWriting('publish');
                    allowLeaveRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onPublish(snapshotValues())];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    err_4 = _a.sent();
                    setSaveErrorMessage(toSaveFailure(err_4, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' })));
                    return [3 /*break*/, 5];
                case 4:
                    setIsSaving(false);
                    stopWriting();
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    /** @see onSubmitForReview */
    var handleSubmitForReview = function () { return __awaiter(void 0, void 0, void 0, function () {
        var err_5;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onSubmitForReview)
                        return [2 /*return*/];
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setShowSubmitForReviewModal(false);
                    setIsSaving(true);
                    startWriting('submit');
                    allowLeaveRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onSubmitForReview(snapshotValues())];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    err_5 = _a.sent();
                    setSaveErrorMessage(toSaveFailure(err_5, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' })));
                    return [3 /*break*/, 5];
                case 4:
                    setIsSaving(false);
                    stopWriting();
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    // Bridge callback exposed to RejectRequestChangesButtons via extraFooterActions.
    // Captures the latest form state at submit time so that edits made after the
    // three-way unsaved-changes dialog opens are still persisted.
    var handleSaveAndRejectFromReject = function (rejection) { return __awaiter(void 0, void 0, void 0, function () {
        var err_6, msg;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!onSaveAndReject)
                        return [2 /*return*/];
                    setValidationErrors({});
                    setSaveErrorMessage(null);
                    setIsSaving(true);
                    allowLeaveRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onSaveAndReject(snapshotValues(), rejection)];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 5];
                case 3:
                    err_6 = _a.sent();
                    // CZNA-1625 — the one exception: a save leg nobody answered in time. Whether the edits
                    // were written is a question about the FORM, and its "Načíst znovu" lives in the form's
                    // banner; RejectRequestChangesButtons closes its modal for this error and shows nothing.
                    if (isSaveOutcomeUnknownError(err_6))
                        setSaveErrorMessage(err_6);
                    msg = err_6 instanceof Error ? err_6.message : intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveError' });
                    throw err_6 instanceof Error ? err_6 : new Error(msg);
                case 4:
                    setIsSaving(false);
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var handleCancel = function () {
        if (isDirty) {
            setShowCancelModal(true);
        }
        else {
            /*
             * Nothing to lose, so nothing to ask about — and while the form is
             * pristine the block is not registered at all, which is why no test can
             * tell this line apart from a bare `onCancel()`.
             *
             * It is routed through the helper for the passive-effect boundary:
             * `reset(snapshot.formValues)` (the record-picker return, above) flips
             * `isDirty` back to false, but the blocker installed by the dirty render
             * stays live until React flushes that effect's cleanup. A cancel landing
             * inside that window sees `isDirty === false` here and a registered
             * blocker there.
             */
            leaveWithoutAsking(onCancel);
        }
    };
    // CZNA-197: in embedded mode the host Modal owns the single X. Expose the
    // latest `handleCancel` so the Modal's close routes through the same
    // unsaved-changes guard as the footer Cancel. Assign on every render to keep
    // the closure (isDirty) fresh.
    if (requestCloseRef) {
        requestCloseRef.current = handleCancel;
    }
    // CZNA-1043 — same reasoning as `requestCloseRef` above: assigned on every
    // render so the closure (`allowLeaveRef`, `history`) stays current.
    if (leaveWithoutAskingRef) {
        leaveWithoutAskingRef.current = leaveWithoutAsking;
    }
    var handleConfirmCancel = function () {
        setShowCancelModal(false);
        // She said "Zahodit". The leave guard does not get to ask again (CZNA-1044).
        leaveWithoutAsking(onCancel);
    };
    var handleCompare = function (candidate) {
        // Control fields come from the loaded record only. The form re-emits them as
        // passthrough rows (CZNA-657), so taking both would print 001 twice — the
        // same filter MarcPreviewPanel applies for the same reason.
        var formFields = filterEmptyPreviewFields(proposalDataToRecordDataFields(genericFormToProposalData(config, getValues())), config).filter(function (f) { return !isControlFieldTag(f.fieldTag); });
        setComparing({ candidate: candidate, draftFields: __spreadArray(__spreadArray([], (previewControlFields !== null && previewControlFields !== void 0 ? previewControlFields : []), true), formFields, true) });
    };
    // CZNA-1466 — "Zahodit rozpracovaný záznam" is the comparison's own answer to
    // "it is a duplicate": the same exit as a confirmed Cancel, nothing saved.
    var handleDiscardFromCompare = function () {
        setComparing(null);
        leaveWithoutAsking(onCancel);
    };
    var resolvedSaveLabel = saveButtonLabelKey
        || (isEdit ? 'ui-czna-authorities.proposalForm.save' : 'ui-czna-authorities.proposalForm.saveDraft');
    var footer = (React.createElement(PaneFooter, { renderStart: React.createElement("div", { style: { display: 'flex', gap: '8px' } },
            React.createElement(Button, { onClick: handleCancel },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.cancel" })),
            onCheck && (React.createElement(Button, { onClick: handleCheck, disabled: isSaving, "data-test-check-button": true },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.check" })))), renderEnd: React.createElement("div", { style: { display: 'flex', gap: '8px' } },
            !hideSaveButton && (React.createElement(Button, { buttonStyle: "primary", onClick: handleSave, disabled: isSaving, "aria-busy": writingAction === 'save', "data-test-record-form-save": true },
                React.createElement(SaveButtonLabel, { saving: writingAction === 'save', labelId: resolvedSaveLabel }))),
            onSaveAndApprove && (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center' } },
                React.createElement(Button, { buttonStyle: "primary", onClick: function () { return setShowSaveAndApproveModal(true); }, disabled: isSaving, "aria-busy": writingAction === 'saveAndApprove' },
                    React.createElement(SaveButtonLabel, { saving: writingAction === 'saveAndApprove', labelId: "ui-czna-authorities.proposalForm.saveAndApprove" })),
                reviewHelpValues && (React.createElement(ActionHelp, { testKey: "saveAndApprove", labelId: "ui-czna-authorities.proposalForm.saveAndApprove", helpId: "ui-czna-authorities.proposalForm.saveAndApprove.effect", helpValues: reviewHelpValues })))),
            onPublish && (React.createElement(Button
            // CZNA-321: the only action left when the plain save is hidden (minimal
            // create), so it takes the primary style the save button would have had.
            , { 
                // CZNA-321: the only action left when the plain save is hidden (minimal
                // create), so it takes the primary style the save button would have had.
                buttonStyle: hideSaveButton ? 'primary' : 'default', onClick: handlePublishClick, disabled: isSaving, "aria-busy": writingAction === 'publish' },
                React.createElement(SaveButtonLabel, { saving: writingAction === 'publish', labelId: "ui-czna-authorities.proposals.detail.publish" }))),
            onSubmitForReview && (React.createElement(Button, { onClick: handleSubmitForReviewClick, disabled: isSaving, "aria-busy": writingAction === 'submit' },
                React.createElement(SaveButtonLabel, { saving: writingAction === 'submit', labelId: "ui-czna-authorities.proposals.detail.submit" }))),
            typeof extraFooterActions === 'function'
                ? extraFooterActions({
                    isDirty: isDirty,
                    isSaving: isSaving,
                    onSaveAndReject: onSaveAndReject ? handleSaveAndRejectFromReject : undefined,
                })
                : extraFooterActions,
            React.createElement(SaveInFlightAnnouncement, { saving: writingAction !== null })) }));
    var paneTitle = paneTitleOverride || headingValue
        || intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.title' });
    var paneSub = paneSubOverride || intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(recordType) });
    // CZNA-321: `activeTabIndex` indexes THIS list, so the picker snapshot (which stores
    // it) and the tab bar agree whether or not the form is narrowed.
    var tabs = shownTabs(config, visibleTabIds);
    var isMinimalCreate = tabs.length < config.tabs.length;
    var activeTab = (_b = tabs[activeTabIndex]) !== null && _b !== void 0 ? _b : tabs[0];
    // CZNA-1451 — "Přejít k poli". The tab switch and the reveal of a collapsed field
    // happen in the render this state triggers; the effect then finds the panel in the
    // committed DOM. A fresh object per click, so a second click on the same row jumps again.
    var _1 = useState(null), pendingJump = _1[0], setPendingJump = _1[1];
    var tabContentRef = useRef(null);
    if (jumpToFieldRef) {
        jumpToFieldRef.current = function (location) {
            var found = locateField(tabs, location.tag);
            if (!found)
                return;
            setActiveTabIndex(found.tabIndex);
            // Under a shared tag the record's occurrence and subfield do not map onto panels in
            // DOM order (an empty ISNI panel precedes the VIAF one) — land on the first instead.
            setPendingJump(found.ambiguous
                ? { tag: location.tag, occurrenceIndex: null, subfieldCode: null, subfieldOccurrence: null }
                : __assign({}, location));
        };
    }
    // CZNA-1468 — a tab click opens the new tab at its start. Only the content changes on a
    // switch; the pane keeps its scroll offset, so a cataloguer who had scrolled down the
    // previous tab landed in the middle of the next one and missed its first field. Set by the
    // tab button alone: "Přejít k poli" switches tabs too, but must end on its field (above),
    // and a click on the tab already open changes nothing. A layout effect, so the middle of
    // the new tab is never painted first.
    var tabBarRef = useRef(null);
    var scrollToTabStartRef = useRef(false);
    useLayoutEffect(function () {
        if (!scrollToTabStartRef.current)
            return;
        scrollToTabStartRef.current = false;
        var bar = tabBarRef.current;
        var content = tabContentRef.current;
        if (!bar || !content)
            return;
        // Scrolled past the start = the content's top has slid under the sticky bar. Scroll back
        // only by that much, so a form that was not scrolled (or a header above the bar) stays.
        var hidden = bar.getBoundingClientRect().bottom - content.getBoundingClientRect().top;
        if (hidden <= 0)
            return;
        var scroller = scrollParentOf(content);
        if (scroller)
            scroller.scrollTop -= hidden;
        else
            window.scrollBy(0, -hidden);
    }, [activeTabIndex]);
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (!pendingJump)
            return;
        setPendingJump(null);
        var panels = Array.from((_b = (_a = tabContentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[data-marc-tag=\"".concat(pendingJump.tag, "\"]"))) !== null && _b !== void 0 ? _b : []);
        var panel = (_d = panels[(_c = pendingJump.occurrenceIndex) !== null && _c !== void 0 ? _c : 0]) !== null && _d !== void 0 ? _d : panels[0];
        if (!panel)
            return;
        var inputs = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
        var subfield = pendingJump.subfieldCode
            ? panel.querySelector("[id^=\"sf-\"][id$=\"-".concat(pendingJump.subfieldCode, "\"]"))
            : null;
        // A multi-value subfield's handle is the <div> holding one row per value: focus the
        // input of the row the issue names, else the first input inside it.
        var valueRow = subfield && !subfield.matches(inputs)
            ? (_f = subfield.children[(_e = pendingJump.subfieldOccurrence) !== null && _e !== void 0 ? _e : 0]) !== null && _f !== void 0 ? _f : null
            : null;
        var target = (_j = (_h = (_g = ((subfield === null || subfield === void 0 ? void 0 : subfield.matches(inputs)) ? subfield : null)) !== null && _g !== void 0 ? _g : valueRow === null || valueRow === void 0 ? void 0 : valueRow.querySelector(inputs)) !== null && _h !== void 0 ? _h : subfield === null || subfield === void 0 ? void 0 : subfield.querySelector(inputs)) !== null && _j !== void 0 ? _j : panel.querySelector(inputs);
        // `scrollIntoView` is guarded for jsdom, as for the save-error banner.
        (_k = panel.scrollIntoView) === null || _k === void 0 ? void 0 : _k.call(panel, { block: 'center' });
        target === null || target === void 0 ? void 0 : target.focus();
    }, [pendingJump]);
    // Save-error banner — shared by both the embedded and route render paths.
    //
    // CZNA-764: `saveError` (parent-supplied) is the fallback, never the override — a failure
    // from the save currently in flight is the newer news than one the parent parked earlier.
    var shownSaveError = saveErrorMessage !== null && saveErrorMessage !== void 0 ? saveErrorMessage : saveError;
    // A new failure object is a new banner: its "Načíst znovu" state starts from nothing.
    var lastFailureRef = useRef(null);
    if (shownSaveError !== lastFailureRef.current) {
        lastFailureRef.current = shownSaveError;
        failureSeqRef.current += 1;
    }
    /**
     * CZNA-1625 — "Načíst znovu" under a save that timed out. The host re-reads the stored entity
     * and, if the write landed, does what a successful save does — usually `history.push` to the
     * detail. That push is the same one `handleSave` lets through the unsaved-changes guard, so
     * it is let through the same way; and the footer is inert while the read runs, so a second
     * "Uložit" cannot race the answer to whether the first one landed.
     */
    var runVerify = function (verify) { return __awaiter(void 0, void 0, void 0, function () {
        var outcome;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setIsSaving(true);
                    allowLeaveRef.current = true;
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, verify()];
                case 2:
                    outcome = _a.sent();
                    // Saved and still here: the host holds the form open (findings, the picker's in-tab
                    // flow) and has already told the cataloguer. The warning is no longer true.
                    if (outcome === 'saved')
                        setSaveErrorMessage(null);
                    return [2 /*return*/, outcome];
                case 3:
                    setIsSaving(false);
                    allowLeaveRef.current = false;
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    // CZNA-764: the banner sits at the top of the form; the save buttons sit in the footer of
    // a form that is routinely taller than the viewport. Rendering the message is therefore not
    // the same as showing it — the cataloguer who clicked "Uložit" at the bottom of a scrolled
    // 100-field form would see nothing change. Bring it to them.
    //
    // `scrollIntoView` is guarded because jsdom does not implement it; the banner is rendered
    // either way, so a missing scroll degrades to the old behaviour rather than throwing.
    useEffect(function () {
        var _a, _b;
        if (!shownSaveError)
            return;
        (_b = (_a = errorBannerRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, { block: 'nearest' });
    }, [shownSaveError]);
    var errorBanner = shownSaveError ? (React.createElement("div", { ref: errorBannerRef, role: "alert", "data-test-save-error-banner": true, style: { background: '#fdecea', marginBottom: '16px', borderRadius: '4px', padding: '16px' } }, typeof shownSaveError === 'string' ? shownSaveError : (React.createElement(SaveOutcomeUnknownNotice, { key: failureSeqRef.current, failure: shownSaveError, runVerify: runVerify })))) : null;
    // Tab bar — shared by both render paths.
    //
    // CZNA-707 (NK, 24. 7. 2026) asked for the record's heading to be visible in
    // the upper part of EVERY tab, and this is where a second copy of it briefly
    // rode. It was dropped after the answer turned out to be one layer up: the
    // pane title never scrolls out of view (`.pane` is a flex column and only
    // `.paneContent` scrolls — which is also why the Save footer stays put), so
    // the reported problem was never the position. It was that the pane title
    // showed NOTHING for a subject heading, because RecordForm composed no
    // heading for the věcné types at all. Fixing the composition fixes what NK
    // hit; a line here as well is the same value twice on one screen.
    var tabBar = (React.createElement("div", { ref: tabBarRef, style: tabBarStyle, "data-test-record-form-tab-bar": true },
        isMinimalCreate && (React.createElement("div", { role: "note", "data-test-minimal-create-hint": true, style: { background: '#e8f1fb', marginBottom: '8px', borderRadius: '4px', padding: '8px 12px' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposalForm.minimalCreateHint" }))),
        React.createElement(ButtonGroup, null, tabs.map(function (tab, idx) {
            // CZNA-1451: errors on a tab the cataloguer is not looking at are otherwise invisible.
            // CZNA-1518: a tab with none shows its warnings instead, in amber.
            var errorCount = countIssuesOnTab(tabs, tab, validationIssues === null || validationIssues === void 0 ? void 0 : validationIssues.filter(function (i) { return i.severity !== 'WARNING'; }));
            var warningCount = errorCount > 0
                ? 0
                : countIssuesOnTab(tabs, tab, validationIssues === null || validationIssues === void 0 ? void 0 : validationIssues.filter(function (i) { return i.severity === 'WARNING'; }));
            var issueCount = errorCount || warningCount;
            var badgeStyle = errorCount > 0 ? tabIssueBadgeStyle : tabWarningBadgeStyle;
            if (validationIssuesStale)
                badgeStyle = tabStaleBadgeStyle;
            return (React.createElement(Button, { key: tab.id, buttonStyle: activeTabIndex === idx ? 'primary' : 'default', onClick: function () {
                    if (idx === activeTabIndex)
                        return;
                    scrollToTabStartRef.current = true;
                    setActiveTabIndex(idx);
                } },
                resolveLabel(tab, intl),
                issueCount > 0 && (React.createElement("span", { "data-test-tab-issue-count": issueCount, "data-test-tab-issue-severity": errorCount > 0 ? 'BLOCKING' : 'WARNING', "data-test-tab-issue-stale": validationIssuesStale || undefined },
                    React.createElement("span", { "aria-hidden": "true", style: badgeStyle }, issueCount),
                    React.createElement("span", { style: visuallyHiddenStyle },
                        intl.formatMessage({
                            id: errorCount > 0
                                ? 'ui-czna-authorities.validation.tab.issueCount'
                                : 'ui-czna-authorities.validation.tab.warningCount',
                        }, { count: issueCount }),
                        validationIssuesStale
                            && " (".concat(intl.formatMessage({ id: 'ui-czna-authorities.validation.tab.staleHint' }), ")"))))));
        }))));
    // Active tab content + MARC preview side-by-side — shared by both render paths.
    // alignItems must stay default (stretch) — the MARC preview panel uses
    // position: sticky and needs its flex column to be as tall as the form column,
    // otherwise there is no scroll room to stick in.
    var tabContent = (React.createElement("div", { style: {
            display: 'flex',
            gap: '24px',
            marginTop: '16px',
        } },
        React.createElement("div", { ref: tabContentRef, style: { flex: '3 1 0%', minWidth: 0 }, "data-test-record-form-tab-content": true },
            React.createElement(SimilarHeadingsHint, { data: similarHeadings.data, isLoading: similarHeadings.isLoading, onCompare: handleCompare }),
            comparing && (React.createElement(DuplicateCompareModal, { candidate: comparing.candidate, draftFields: comparing.draftFields, onContinue: function () { return setComparing(null); }, onDiscard: handleDiscardFromCompare })),
            headingPreview.settled && React.createElement(HeadingRequiredHint, { headingValue: headingValue }),
            React.createElement(SubfieldCatalogProvider, null,
                React.createElement(DynamicTab, { tab: activeTab, control: control, watch: watch, getValues: getValues, setValue: setValue, showToast: showToast, errors: validationErrors, mode: mode, targetRecordAuthorityIdDisplay: targetRecordAuthorityIdDisplay, revealFieldTag: pendingJump === null || pendingJump === void 0 ? void 0 : pendingJump.tag })),
            activeTab.id === 'codedData' && isEdit && metadata && (React.createElement(ProcessorInfo, { createdBy: metadata.createdBy, createdAt: metadata.createdAt, updatedBy: metadata.updatedBy, updatedAt: metadata.updatedAt }))),
        isWideViewport && (React.createElement("div", { style: { flex: '2 1 0%', minWidth: '300px' } },
            React.createElement(MarcPreviewPanel, { config: config, watch: watch, controlFields: previewControlFields, recordType: previewRecordType })))));
    // Modals + Callout — shared by both render paths. The close-guard
    // ConfirmationModal MUST render in embedded mode too, so the host Modal's X
    // (routed through requestCloseRef → handleCancel) can open it on unsaved edits.
    var overlays = (React.createElement(React.Fragment, null,
        React.createElement(Modal, { open: pendingLeave !== null, label: intl.formatMessage({ id: leaveModalHeadingKey }), onClose: function () { return setPendingLeave(null); }, dismissible: true, size: "small", footer: (React.createElement(ButtonGroup, null,
                React.createElement(Button, { onClick: function () { return setPendingLeave(null); } }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.leaveModal.cancel' })),
                React.createElement(Button, { onClick: handleConfirmLeave }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.leaveModal.confirm' })),
                React.createElement(Button, { buttonStyle: "primary", onClick: handleSaveAndLeave }, intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.leaveModal.saveAndLeave' })))) }, intl.formatMessage({ id: leaveModalMessageKey })),
        React.createElement(ConfirmationModal, { open: showCancelModal, heading: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.cancelModal.heading' }), message: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.cancelModal.message' }), confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.cancelModal.confirm' }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.cancelModal.cancel' }), onConfirm: handleConfirmCancel, onCancel: function () { return setShowCancelModal(false); } }),
        onSaveAndApprove && (React.createElement(ConfirmationModal, { open: showSaveAndApproveModal, heading: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveAndApproveModal.heading' }), message: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveAndApproveModal.message' }), confirmLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveAndApproveModal.confirm' }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposalForm.saveAndApproveModal.cancel' }), onConfirm: handleSaveAndApprove, onCancel: function () { return setShowSaveAndApproveModal(false); } })),
        onPublish && (React.createElement(ConfirmationModal, { open: showPublishModal, heading: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.publishModal.heading' }), 
            // A div, not the default <p>: the notice carries a list.
            bodyTag: confirmNotice ? 'div' : undefined, message: confirmNotice ? (React.createElement(React.Fragment, null,
                React.createElement("p", null, intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.publishModal.message' })),
                confirmNotice)) : intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.publishModal.message' }), confirmLabel: intl.formatMessage({
                id: confirmNotice
                    ? 'ui-czna-authorities.proposalForm.confirm.publishAnyway'
                    : 'ui-czna-authorities.proposals.detail.publishModal.confirm',
            }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.publishModal.cancel' }), onConfirm: handlePublish, onCancel: function () { return setShowPublishModal(false); } })),
        onSubmitForReview && (React.createElement(ConfirmationModal, { open: showSubmitForReviewModal, heading: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.submitModal.heading' }), bodyTag: confirmNotice ? 'div' : undefined, message: confirmNotice ? (React.createElement(React.Fragment, null,
                React.createElement("p", null, intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.submitModal.message' })),
                confirmNotice)) : intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.submitModal.message' }), confirmLabel: intl.formatMessage({
                id: confirmNotice
                    ? 'ui-czna-authorities.proposalForm.confirm.submitAnyway'
                    : 'ui-czna-authorities.proposals.detail.submitModal.confirm',
            }), cancelLabel: intl.formatMessage({ id: 'ui-czna-authorities.proposals.detail.submitModal.cancel' }), onConfirm: handleSubmitForReview, onCancel: function () { return setShowSubmitForReviewModal(false); } })),
        React.createElement(Callout, { ref: calloutRef })));
    // CZNA-197 (SF-1): when hosted inside a Modal (embedded), render NO
    // Paneset/Pane chrome at all. A root Paneset is absolutely positioned to fill
    // the viewport (collapses to zero height inside a Modal body); a non-root
    // Paneset joins the PARENT editor's PanesetContext and registers a phantom
    // pane there, corrupting the parent layout (parent shrinks to the left half,
    // right half goes blank). Instead we render a plain fixed-height,
    // vertically-scrollable container: the Modal already owns the header + close
    // (X), so we only need the error banner + tab bar + tab content (scrolling)
    // and the footer (pinned at the bottom). The close-guard ConfirmationModal is
    // rendered via `overlays` so the Modal's X (→ requestCloseRef → handleCancel)
    // still works.
    if (embedded) {
        return (React.createElement(ProposeAuthorityProvider, { proposeAuthority: proposeAuthority },
            React.createElement("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '70vh',
                    maxHeight: '70vh',
                    // CZNA-1518 — the check panel positions itself against this box.
                    position: 'relative',
                } },
                React.createElement("div", { style: { flex: '1 1 auto', overflow: 'auto', padding: '0 16px' } },
                    headerActions && (React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end', padding: '8px 0' } }, headerActions)),
                    banners,
                    errorBanner,
                    tabBar,
                    tabContent),
                React.createElement("div", { style: { flex: '0 0 auto', borderTop: '1px solid #e0e0e0' } }, footer),
                checkPanel,
                overlays)));
    }
    return (React.createElement(ProposeAuthorityProvider, { proposeAuthority: proposeAuthority },
        React.createElement(Paneset, { isRoot: true },
            React.createElement(Pane, { id: paneId, defaultWidth: "fill", paneTitle: paneTitle, paneSub: paneSub, dismissible: true, onClose: handleCancel, lastMenu: headerActions ? React.createElement(PaneMenu, null, headerActions) : undefined, footer: footer },
                banners,
                errorBanner,
                tabBar,
                tabContent),
            checkPanel,
            overlays)));
};
export default RecordForm;
